/**
 * Column-width persistence + drag math for the TaskListPage table.
 *
 * The table is `table-fixed`, so a column's width is governed by its
 * header cell in the first (thead) row. We persist each user-dragged
 * width to localStorage (browser-scoped, no server round-trip) and clamp
 * every value to a hard [60, 1000] px range so a corrupt entry or a
 * runaway drag can't wedge the layout.
 *
 * Keeping the math here (pure, DOM-free) lets the vitest `node` project
 * exercise the resize / restore / clamp contracts without a browser —
 * the same static-test strategy the rest of the page suite uses.
 */

export type TaskListColumnKey =
  | 'sourceTaskId'
  | 'status'
  | 'category'
  | 'name'
  | 'area'
  | 'sysFrom'
  | 'sysTo'
  | 'assignee'
  | 'executionType'
  | 'plannedStartDate'
  | 'completedAt'
  | 'description'
  | 'backlog';

/**
 * Visual left-to-right column order. Drives the `th:nth-child(n)` width
 * rules, so it MUST match the header cell order in TaskListPage.tsx.
 *
 * Order (matches the xlsx-aligned spec): No · ステータス · カテゴリ ·
 * タスク名 · 領域 · sys From · sys To · 担当 · 方式 · 予定開始日 ·
 * 完了日 · 説明 — with the Backlog meta column pinned last.
 */
export const TASK_LIST_COLUMN_ORDER: readonly TaskListColumnKey[] = [
  'sourceTaskId',
  'status',
  'category',
  'name',
  'area',
  'sysFrom',
  'sysTo',
  'assignee',
  'executionType',
  'plannedStartDate',
  'completedAt',
  'description',
  'backlog',
];

/**
 * localStorage key for the persisted width map. Versioned so a column-set
 * change can bump the suffix and fall back to defaults instead of reading
 * a stale shape. Bumped to `.v2` when the 領域 / sys From / sys To columns
 * were added and the order was reshuffled — `.v1` entries are ignored.
 */
export const TASK_LIST_COLUMN_WIDTHS_KEY = 'migratus.taskList.columnWidths.v2';

/** Hard clamp range (px) applied to every single column width. */
export const MIN_COLUMN_WIDTH = 60;
export const MAX_COLUMN_WIDTH = 1000;

/**
 * Default widths (px) reproducing the pre-resize table-fixed layout.
 * `name` is intentionally omitted: with no explicit width it stays the
 * remainder-absorbing (auto) column — the current spec — until the user
 * explicitly drags it.
 */
export const DEFAULT_COLUMN_WIDTHS: Readonly<
  Partial<Record<TaskListColumnKey, number>>
> = {
  sourceTaskId: 84,
  status: 110,
  category: 160,
  area: 110,
  sysFrom: 100,
  sysTo: 100,
  assignee: 90,
  executionType: 80,
  plannedStartDate: 110,
  completedAt: 110,
  description: 280,
  backlog: 110,
};

export type ColumnWidthMap = Partial<Record<TaskListColumnKey, number>>;

/** Clamp + round a width to the hard [MIN, MAX] px range. */
export function clampColumnWidth(width: number): number {
  if (!Number.isFinite(width)) return MIN_COLUMN_WIDTH;
  return Math.max(
    MIN_COLUMN_WIDTH,
    Math.min(MAX_COLUMN_WIDTH, Math.round(width)),
  );
}

/**
 * Width during a drag: the column's width when the drag began plus the
 * pointer's horizontal delta, clamped to the hard range. This is the
 * value committed on every `mousemove` tick.
 */
export function nextWidthFromDrag(startWidth: number, deltaX: number): number {
  return clampColumnWidth(startWidth + deltaX);
}

/**
 * Effective width for a column: a stored override if present, else the
 * built-in default. Returns `undefined` when neither exists (i.e. the
 * `name` column at rest) so the caller can leave it `width: auto`.
 */
export function resolveColumnWidth(
  widths: ColumnWidthMap,
  key: TaskListColumnKey,
): number | undefined {
  const stored = widths[key];
  if (stored != null) return clampColumnWidth(stored);
  return DEFAULT_COLUMN_WIDTHS[key];
}

type StorageLike = Pick<Storage, 'getItem' | 'setItem'>;

function resolveStorage(storage?: StorageLike): StorageLike | null {
  if (storage) return storage;
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

/**
 * Read persisted widths, dropping unknown keys and out-of-range / non
 * numeric values so a corrupt entry degrades to defaults rather than
 * breaking the table.
 */
export function loadColumnWidths(storage?: StorageLike): ColumnWidthMap {
  const store = resolveStorage(storage);
  if (!store) return {};
  let raw: string | null;
  try {
    raw = store.getItem(TASK_LIST_COLUMN_WIDTHS_KEY);
  } catch {
    return {};
  }
  if (!raw) return {};
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return {};
  }
  if (!parsed || typeof parsed !== 'object') return {};
  const out: ColumnWidthMap = {};
  const source = parsed as Record<string, unknown>;
  for (const key of TASK_LIST_COLUMN_ORDER) {
    const value = source[key];
    if (typeof value === 'number' && Number.isFinite(value)) {
      out[key] = clampColumnWidth(value);
    }
  }
  return out;
}

/** Persist the width map. Swallows quota / serialization failures. */
export function saveColumnWidths(
  widths: ColumnWidthMap,
  storage?: StorageLike,
): void {
  const store = resolveStorage(storage);
  if (!store) return;
  try {
    store.setItem(TASK_LIST_COLUMN_WIDTHS_KEY, JSON.stringify(widths));
  } catch {
    /* ignore quota / serialization errors — widths are best-effort */
  }
}

/**
 * Build the scoped CSS that applies each column's width (and the
 * `position: relative` the drag handles anchor to) via
 * `th:nth-child(n)`. Driving widths through a stylesheet instead of
 * per-`th` `style`/`width` attributes keeps the existing source-text
 * markup tests intact and avoids the phantom-column trap a `<colgroup>`
 * hits when responsive-hidden cells leave a `<col>` orphaned.
 */
export function buildColumnWidthCss(
  widths: ColumnWidthMap,
  tableTestId: string,
): string {
  return TASK_LIST_COLUMN_ORDER.map((key, index) => {
    const width = resolveColumnWidth(widths, key);
    const decls =
      width != null ? `position:relative;width:${width}px;` : 'position:relative;';
    return `[data-testid="${tableTestId}"]>thead>tr>th:nth-child(${index + 1}){${decls}}`;
  }).join('');
}
